import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useI18n } from '../../hooks/useI18n';
import { subscriberApi } from '../../services/subscriberApi';
import { Subscriber } from '../../types';
import { Plus, Edit, Trash2, Save, X, ArrowLeft } from 'lucide-react';
import Layout from '../../components/Layout';

const EntitySubscribersPage: React.FC = () => {
    const { entityConfigId } = useParams<{ entityConfigId: string }>();
    const navigate = useNavigate();
    const { t, isRTL } = useI18n();
    const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentSubscriber, setCurrentSubscriber] = useState<Partial<Subscriber>>({});
    const [isEditing, setIsEditing] = useState(false);
    const [password, setPassword] = useState('');

    useEffect(() => {
        if (entityConfigId) {
            fetchSubscribers();
        }
    }, [entityConfigId]);

    const fetchSubscribers = async () => {
        try {
            setLoading(true);
            if (entityConfigId) {
                const data = await subscriberApi.getSubscribersByEntityConfigId(entityConfigId);
                setSubscribers(data);
            }
        } catch (err) {
            setError('Failed to fetch subscribers');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setCurrentSubscriber({
            email: '',
            login: '',
            active: true,
            entityConfigId: entityConfigId
        });
        setPassword('');
        setIsEditing(false);
        setIsModalOpen(true);
    };

    const handleEdit = (subscriber: Subscriber) => {
        setCurrentSubscriber(subscriber);
        setPassword('');
        setIsEditing(true);
        setIsModalOpen(true);
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Are you sure you want to delete this subscriber?')) {
            try {
                await subscriberApi.deleteSubscriber(id);
                fetchSubscribers();
            } catch (err) {
                setError('Failed to delete subscriber');
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const subscriberData = { ...currentSubscriber };
            if (password) {
                // @ts-ignore
                subscriberData.password = password;
            }

            if (isEditing && currentSubscriber.id) {
                await subscriberApi.updateSubscriber(currentSubscriber.id, subscriberData);
            } else {
                await subscriberApi.createSubscriber(subscriberData);
            }
            setIsModalOpen(false);
            fetchSubscribers();
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to save subscriber';
            setError(errorMessage);
        }
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <Layout>
            <div className="p-6">
                <div className="flex items-center gap-4 mb-6">
                    <button
                        onClick={() => navigate('/admin/entity-configs')}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <ArrowLeft className={`h-5 w-5 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
                    </button>
                    <h1 className="text-2xl font-bold text-gray-800">
                        Subscribers for Entity: {entityConfigId}
                    </h1>
                    <div className="flex-grow"></div>
                    <button
                        onClick={handleCreate}
                        className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Plus size={20} />
                        Add Subscriber
                    </button>
                </div>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 relative">
                        <span className="block sm:inline">{error}</span>
                        <span className="absolute top-0 bottom-0 right-0 px-4 py-3" onClick={() => setError(null)}>
                            <X size={18} className="cursor-pointer" />
                        </span>
                    </div>
                )}

                <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 border-b border-gray-100">
                            <tr>
                                <th className="p-4 font-semibold text-gray-600">Login</th>
                                <th className="p-4 font-semibold text-gray-600">Email</th>
                                <th className="p-4 font-semibold text-gray-600">Status</th>
                                <th className="p-4 font-semibold text-gray-600">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {subscribers.map((subscriber) => (
                                <tr key={subscriber.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="p-4">{subscriber.login}</td>
                                    <td className="p-4">{subscriber.email}</td>
                                    <td className="p-4">
                                        <span className={`px-2 py-1 rounded-full text-xs ${subscriber.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                            {subscriber.active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="p-4">
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleEdit(subscriber)}
                                                className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                                                title="Edit"
                                            >
                                                <Edit size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(subscriber.id)}
                                                className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                                                title="Delete"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {subscribers.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="p-8 text-center text-gray-500">
                                        No subscribers found for this entity.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {isModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 m-4">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-bold text-gray-800">
                                    {isEditing ? 'Edit Subscriber' : 'New Subscriber'}
                                </h2>
                                <button onClick={() => setIsModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                                    <X size={24} />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Login</label>
                                    <input
                                        type="text"
                                        required
                                        value={currentSubscriber.login || ''}
                                        onChange={(e) => setCurrentSubscriber({ ...currentSubscriber, login: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                    <input
                                        type="email"
                                        required
                                        value={currentSubscriber.email || ''}
                                        onChange={(e) => setCurrentSubscriber({ ...currentSubscriber, email: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {isEditing ? 'New Password (leave blank to keep current)' : 'Password'}
                                    </label>
                                    <input
                                        type="password"
                                        required={!isEditing}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="active"
                                        checked={currentSubscriber.active !== false}
                                        onChange={(e) => setCurrentSubscriber({ ...currentSubscriber, active: e.target.checked })}
                                        className="rounded text-blue-600 focus:ring-blue-500"
                                    />
                                    <label htmlFor="active" className="text-sm font-medium text-gray-700">Active</label>
                                </div>

                                <div className="flex justify-end gap-3 mt-6">
                                    <button
                                        type="button"
                                        onClick={() => setIsModalOpen(false)}
                                        className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                                    >
                                        <Save size={18} />
                                        Save Subscriber
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default EntitySubscribersPage;
